import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { Plus, Trash2, X } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ADMIN_ID } from "./Constants";

const actionTypeOptions = [
    { value: 'call', label: 'Call' },
    { value: 'link', label: 'Link' },
];

const BlogModal = ({ isOpen, setIsOpen, onSubmit, initialData = null }) => {
    const [formData, setFormData] = useState({
        title: '',
        author: '',
        category: '',
        image1: null,
        image2: null,
        image1Preview: '',
        image2Preview: '',
        paragraphs: [''],
        actionType: 'link',
        actionValue: ''
    });
    const [isLoading, setIsLoading] = useState(false);


    //const apiUrl = import.meta.env.VITE_API_URL;


    useEffect(() => {
        if (initialData) {
            // Ensure the image previews are properly set
            setFormData({
                title: initialData.title || '',
                author: initialData.authorName || '', // FIXED: use authorName
                category: initialData.category || '',
                image1: null,
                image2: null,
                image1Preview: initialData.image1 ? `http://localhost:8000/${initialData.image1}` : '',
                image2Preview: initialData.image2 ? `http://localhost:8000/${initialData.image2}` : '',
                paragraphs: initialData.description?.length ? initialData.description : [''],
                actionType: initialData.actionType || 'link',
                actionValue: initialData.actionValue || ''
            });
        } else {
            setFormData({
                title: '',
                author: '',
                category: '',
                image1: null,
                image2: null,
                image1Preview: '',
                image2Preview: '',
                paragraphs: [''],
                actionType: 'link',
                actionValue: ''
            });
        }
    }, [initialData]);

    if (!isOpen) return null;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e, field) => {
        const file = e.target.files[0];
        if (file) {
            const preview = URL.createObjectURL(file);
            setFormData((prev) => ({
                ...prev,
                [field]: file,
                [`${field}Preview`]: preview
            }));
        }
    };

    const handleParagraphChange = (index, value) => {
        const updatedParagraphs = [...formData.paragraphs];
        updatedParagraphs[index] = value;
        setFormData((prev) => ({ ...prev, paragraphs: updatedParagraphs }));
    };

    const addParagraph = () => {
        setFormData((prev) => ({ ...prev, paragraphs: [...prev.paragraphs, ''] }));
    };

    const removeParagraph = (index) => {
        const updated = formData.paragraphs.filter((_, i) => i !== index);
        setFormData((prev) => ({ ...prev, paragraphs: updated }));
    };

    const handleActionTypeChange = (option) => {
        setFormData((prev) => ({
            ...prev,
            actionType: option.value,
            actionValue: ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);


        const slug = formData.title.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '');


        const payload = new FormData();
        payload.append('slug', slug);

        payload.append('title', formData.title);
        payload.append('authorName', formData.author);
        payload.append('category', formData.category);
        payload.append('description', JSON.stringify(formData.paragraphs));
        payload.append('actionType', formData.actionType);
        payload.append('actionValue', formData.actionValue);
        payload.append('AdminId', ADMIN_ID); // ✅ Add this line

        if (formData.image1) payload.append('image1', formData.image1);
        if (formData.image2) payload.append('image2', formData.image2);

        try {
            const res = await fetch(
                initialData
                    ? `http://localhost:8000/api/blogs/${initialData.id}`
                    : `http://localhost:8000/api/blogs`,
                {
                    method: initialData ? 'PUT' : 'POST',
                    body: payload
                }
            );

            if (!res.ok) throw new Error(await res.text());
            const saved = await res.json();

            toast.success(initialData ? '✅ Blog updated' : '✅ Blog created');
            setTimeout(() => {
                setIsOpen(false);
                onSubmit?.(saved); // Reload blog table
            }, 1000);
        } catch (err) {
            console.error('Blog submit failed:', err);
            toast.error('❌ Something went wrong.');
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[99999] flex flex-col justify-end items-center" onClick={() => setIsOpen(false)}>
            <div className="bg-white w-full max-h-[90vh] max-w-[600px] rounded-t-2xl shadow-xl p-5 overflow-y-auto transition-transform duration-300 animate-slide-up" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">Add Blog</h2>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="text-gray-500 hover:text-red-500 text-sm font-medium"
                    >
                        <X />
                    </button>
                </div>

                <form className="space-y-4" onSubmit={handleSubmit}>
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-black mb-2">Title</label>
                        <input
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter a Blog title"
                            className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-1 focus:ring-blue-400 outline-none"
                        />
                    </div>

                    {/* Author */}
                    <div>
                        <label className="block text-sm font-medium text-black mb-2">Author Name</label>
                        <input
                            name="author"
                            value={formData.author}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter Author Name"
                            className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-1 focus:ring-blue-400 outline-none"
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-black mb-2">Category</label>
                        <input
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter cogegory"
                            className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-1 focus:ring-blue-400 outline-none"
                        />
                    </div>

                    {/* Image 1 */}
                    <div>
                        <label className="block text-sm font-medium text-black mb-2">Image 1 (Banner)</label>
                        <input
                            className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-1 focus:ring-blue-400 outline-none"
                            type="file" accept="image/*" onChange={(e) => handleImageChange(e, 'image1')} />
                        {formData.image1Preview && (
                            <img
                                src={formData.image1Preview}
                                alt="Preview"
                                className="w-[100px] h-auto rounded-lg shadow-md object-cover mt-2"
                            />
                        )}

                    </div>

                    {/* Paragraphs */}
                    <div>
                        <label className="block text-sm font-medium text-black mb-2">Paragraphs</label>
                        {formData.paragraphs.map((text, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                <textarea
                                    value={text}
                                    onChange={(e) => handleParagraphChange(idx, e.target.value)}
                                    required
                                    rows={3}
                                    placeholder="Write blog content here..."
                                    className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-1 focus:ring-blue-400 outline-none"
                                />
                                {formData.paragraphs.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeParagraph(idx)}
                                        title='Remove Paragraph'
                                        className="flex items-center gap-1 text-red-600 hover:underline cursor-pointer text-sm"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                )}

                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={addParagraph}
                            className="flex items-center gap-1 text-blue-600 hover:underline cursor-pointer text-sm font-medium"
                        >
                            <Plus size={16} />
                            Add Paragraph
                        </button>

                    </div>

                    {/* Image 2 */}
                    <div>
                        <label className="block text-sm font-medium text-black mb-2">Image 2 (Optional)</label>
                        <input
                            className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-1 focus:ring-blue-400 outline-none"
                            type="file" accept="image/*" onChange={(e) => handleImageChange(e, 'image2')} />
                        {formData.image2Preview && (
                            <img
                                src={formData.image2Preview}
                                alt="Preview"
                                className="w-[100px] h-auto rounded-lg shadow-md object-cover mt-2"
                            />
                        )}
                    </div>

                    {/* Action Type */}
                    <div>
                        <label className="block text-sm font-medium text-black mb-2">
                            Action Type
                        </label>
                        <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="actionType"
                                    value="call"
                                    checked={formData.actionType === "call"}
                                    onChange={(e) =>
                                        setFormData({ ...formData, actionType: e.target.value, actionValue: "" })
                                    }
                                />
                                <span>Call</span>
                            </label>

                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="actionType"
                                    value="link"
                                    checked={formData.actionType === "link"}
                                    onChange={(e) =>
                                        setFormData({ ...formData, actionType: e.target.value, actionValue: "" })
                                    }
                                />
                                <span>Link</span>
                            </label>
                        </div>
                    </div>

                    {/* Action Value (conditional) */}
                    {formData.actionType && (
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-black mb-2">
                                {formData.actionType === "call" ? "Phone Number" : "URL"}
                            </label>
                            <input
                                name="actionValue"
                                value={formData.actionValue}
                                onChange={handleInputChange}
                                type={formData.actionType === "call" ? "tel" : "url"}
                                placeholder={
                                    formData.actionType === "call"
                                        ? "e.g. +91 9876543210"
                                        : "e.g. https://yourwebsite.com/blog-post"
                                }
                                required
                                className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-1 focus:ring-blue-400 outline-none"
                            />
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-end gap-3 pt-2 mt-5">
                        <button type="submit" disabled={isLoading}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:bg-blue-300 disabled:cursor-not-allowed">
                            {isLoading ? 'Saving...' : initialData ? 'Update' : 'Submit'}
                        </button>

                    </div>
                </form>
                <ToastContainer position="top-center" autoClose={3000} hideProgressBar />

            </div>
        </div>
    );
};

export default BlogModal;
